import { useEffect, useMemo, useRef, useState } from "react";
import { DialogActions } from "ui/lexical-editor/ui/dialog";
import TextInput from "ui/lexical-editor/ui/text-input";
import Select from "ui/lexical-editor/ui/select";
import { useGetMedias } from "pages/section-management/media-management/api";
import InfiniteSelect from "ui/infinite-select";
import { Col, Flex, Row } from "antd";
import { LexicalEditor } from "lexical";
import { CTABorderStyle, CTAPosition, INSERT_CALL_TO_ACTION_COMMAND, InsertCallToActionPayload } from ".";
import { Button } from "ui/button";
import CheckboxInput from "ui/lexical-editor/ui/checkbox-input";

export function InsertCallToActionDialog({
    activeEditor,
    onClose,
}: {
    activeEditor: LexicalEditor;
    onClose: () => void;
}): JSX.Element {
    const hasModifier = useRef(false);

    useEffect(() => {
        hasModifier.current = false;
        const handler = (e: KeyboardEvent) => {
            hasModifier.current = e.altKey;
        };
        document.addEventListener("keydown", handler);
        return () => {
            document.removeEventListener("keydown", handler);
        };
    }, [activeEditor]);

    const onClick = (payload: InsertCallToActionPayload) => {
        //@ts-ignore
        activeEditor.dispatchCommand(INSERT_CALL_TO_ACTION_COMMAND, payload);
        onClose();
    };

    const [mediasPage, setMediasPage] = useState<number>(1);
    const [mediasSearch, setMediasSearch] = useState<string>("");
    const [src, setSrc] = useState<string>();
    const [mobileSrc, setMobileSrc] = useState<string>();
    const [actionLink, setActionLink] = useState<string>();
    const [actionText, setActionText] = useState("برای خرید کلیک کنید");
    const [actionTextColor, setActionTextColor] = useState("#000");
    const [actionTextSize, setActionTextSize] = useState("18");
    const [actionButtonColor, setActionButtonColor] = useState("#000");
    const [actionButtonSize, setActionButtonSize] = useState("35");
    const [actionPosition, setActionPosition] = useState<CTAPosition>("end");
    const [borderStyle, setBorderStyle] = useState<CTABorderStyle>("normal");
    const [showPrice, setShowPrice] = useState(false);
    const [priceTextColor, setPriceTextColor] = useState("#000");
    const [priceTextSize, setPriceTextSize] = useState("16");
    const [priceVPosition, setPriceVPosition] = useState<CTAPosition>("end");
    const [priceHPosition, setPriceHPosition] = useState<CTAPosition>("start");

    const isDisabled = src === "";

    const { data: medias, isPending: fetchingMedias } = useGetMedias({
        page: mediasPage,
        pageSize: 10,
        inputParams: {
            filters: [
                {
                    propertyName: "title",
                    operation: "contains",
                    value: mediasSearch
                }
            ],
            sort: {
                propertyName: "id",
                ascending: false,
            },
        },
    });
    const mediasOptions = useMemo(
        () =>
            (medias?.data?.data ?? []).map((item: any) => ({
                label: item.title,
                value: item,
            })),
        [medias]
    );

    return (
        <>
            <Row gutter={16}>
                <Col span={12}>
                    <Flex justify="space-between" align="center" className="mb-3">
                        <label style={{ color: "#666", flex: `1` }}>لینک تصویر:</label>
                        <div className="w-100" style={{ flex: `4.3` }}>
                            <InfiniteSelect
                                args={{}}
                                variation="flat"
                                placeholder="انتخاب تصویر"
                                onChange={(item: any) => {
                                    setSrc(`${process.env.REACT_APP_PUBLISH_WEBSITE_BASE_URL}${item?.value?.image?.url}`);
                                    setMobileSrc(`${process.env.REACT_APP_PUBLISH_WEBSITE_BASE_URL}${item?.value?.mobileImage?.url}`);
                                }}
                                options={mediasOptions}
                                onInputChange={(phrase) => {
                                    setMediasSearch(phrase);
                                }}
                                isLoading={fetchingMedias}
                                onPageChange={(page) => setMediasPage(page)}
                                page={mediasPage}
                                isClearable={true}
                            />
                        </div>
                    </Flex>
                </Col>
                <Col span={12}>
                    <Flex justify="space-between" align="center" className="w-100 pt-2">
                        <Select
                            style={{ minWidth: "95px" }}
                            label="حاشیه تصویر:"
                            labelTop="-0.3em"
                            labelGap="0.6rem"
                            name="borderStyle"
                            id="border-style-select"
                            value={borderStyle}
                            onChange={(e) => setBorderStyle(e.target.value as CTABorderStyle)}
                        >
                            <option value="normal">ساده</option>
                            <option value="rounded">گرد</option>
                            <option value="flat">فلت</option>
                        </Select>
                        <Select
                            style={{ minWidth: "95px" }}
                            label="موقعیت اکشن:"
                            labelTop="-0.3em"
                            labelGap="0.6rem"
                            name="position"
                            id="position-select"
                            value={actionPosition}
                            onChange={(e) => setActionPosition(e.target.value as CTAPosition)}
                        >
                            <option value="end">چپ</option>
                            <option value="start">راست</option>
                            <option value="center">وسط</option>
                        </Select>
                    </Flex>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <TextInput
                        label="متن اکشن:"
                        onChange={setActionText}
                        value={actionText}
                        data-test-id="CTA-modal-action-text-input"
                        labelSpan={4}
                    />
                </Col>
                <Col span={12}>
                    <TextInput
                        label="لینک اکشن:"
                        onChange={(e) => {
                            const link = e.startsWith("http") ? e : `https://${e}`;
                            setActionLink(link);
                        }}
                        placeholder="https://"
                        value={actionLink}
                        data-test-id="CTA-modal-action-text-input"
                        className="dir-ltr"
                        labelSpan={4}
                    />
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <TextInput
                        label="رنگ اکشن:"
                        onChange={setActionTextColor}
                        value={actionTextColor}
                        data-test-id="CTA-modal-action-text-color-input"
                        labelSpan={4}
                    />
                </Col>
                <Col span={12}>
                    <TextInput
                        label="سایز اکشن:"
                        type="number"
                        min={1}
                        max={33}
                        onChange={setActionTextSize}
                        value={actionTextSize}
                        data-test-id="CTA-modal-action-text-size-input"
                        labelSpan={4}
                    />
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <TextInput
                        label="رنگ دکمه:"
                        onChange={setActionButtonColor}
                        value={actionButtonColor}
                        data-test-id="CTA-modal-action-text-color-input"
                        labelSpan={4}
                    />
                </Col>
                <Col span={12}>
                    <TextInput
                        label="سایز دکمه:"
                        type="number"
                        min={1}
                        max={33}
                        onChange={setActionButtonSize}
                        value={actionButtonSize}
                        data-test-id="CTA-modal-action-text-size-input"
                        labelSpan={4}
                    />
                </Col>
            </Row>
            <Row className="mt-2">
                <Col span={24} className="pt-1">
                    <Flex justify="space-between" align="center">
                        <div className="mt-1" style={{ width: '14%' }}>
                            <CheckboxInput
                                labelSpan={1}
                                label="نمایش قیمت:"
                                checked={showPrice}
                                onChange={setShowPrice}
                            />
                        </div>
                        {showPrice && (
                            <>
                                <Select
                                    style={{ minWidth: "80px" }}
                                    label="موقعیت افقی:"
                                    labelTop="0.1em"
                                    labelGap="0.5rem"
                                    name="price-h-position"
                                    id="price-h-position-select"
                                    value={priceHPosition}
                                    onChange={(e) => setPriceHPosition(e.target.value as CTAPosition)}
                                >
                                    <option value="end">چپ</option>
                                    <option value="start">راست</option>
                                    <option value="center">وسط</option>
                                </Select>
                                <Select
                                    style={{ minWidth: "80px" }}
                                    label="موقعیت عمودی:"
                                    labelTop="0.1em"
                                    labelGap="0.5rem"
                                    name="price-v-position"
                                    id="price-v-position-select"
                                    value={priceVPosition}
                                    onChange={(e) => setPriceVPosition(e.target.value as CTAPosition)}
                                >
                                    <option value="start">بالا</option>
                                    <option value="end">پایین</option>
                                    <option value="center">وسط</option>
                                </Select>
                                <div style={{ width: '150px' }}>
                                    <TextInput
                                        label="سایز قیمت:"
                                        type="number"
                                        min={1}
                                        max={45}
                                        onChange={setPriceTextSize}
                                        value={priceTextSize}
                                        data-test-id="CTA-modal-price-text-size-input"
                                        labelSpan={0.7}
                                    />
                                </div>
                                <div style={{ width: '150px' }}>
                                    <TextInput
                                        label="رنگ قیمت:"
                                        type="text"
                                        onChange={setPriceTextColor}
                                        value={priceTextColor}
                                        data-test-id="CTA-modal-price-text-color-input"
                                        labelSpan={0.7}
                                    />
                                </div>
                            </>
                        )}
                    </Flex>
                </Col>
            </Row>
            <DialogActions>
                <Flex justify="center" className="w-100">
                    <Button
                        data-test-id="CTA-modal-file-upload-btn"
                        disabled={isDisabled}
                        type="primary"
                        size="large"
                        onClick={() => {
                            if ((src?.trim()?.length ?? 0) > 0) {
                                onClick({
                                    imgAddress: src!,
                                    mobileImgAddress: (mobileSrc?.trim()?.length ?? 0) > 0 ? mobileSrc! : src!,
                                    actionLink: actionLink ?? "/",
                                    actionText,
                                    actionTextColor,
                                    actionTextSize,
                                    actionButtonColor,
                                    actionButtonSize,
                                    actionPosition,
                                    borderStyle,
                                    showPrice,
                                    priceTextColor,
                                    priceTextSize,
                                    priceHPosition,
                                    priceVPosition
                                });
                            }
                        }}
                    >
                        ایجاد
                    </Button>
                </Flex>
            </DialogActions>
        </>
    );
}